/**
 * Per-supplier append-only hardware/fastener price history for
 * materialsDatabase_hardware. Current unitPrice stays on the supplierPricing
 * entry; priceHistory records prior points (oldest → newest). Cap at 50.
 *
 * Mirrors timberSupplierPriceHistory (piecePrice/costPerBdFt → unitPrice).
 */

export const HARDWARE_PRICE_HISTORY_CAP = 50;

export type HardwarePriceHistorySource =
  | 'manual'
  | 'import'
  | 'materials_price_approve'
  | 'seed';

export interface HardwarePricePointLike {
  unitPrice: number;
  recordedAt: Date | string | number | { toDate?: () => Date; seconds?: number };
  source?: HardwarePriceHistorySource | string;
}

export interface HardwareSupplierPriceWithHistoryLike {
  supplier: string;
  unitPrice: number;
  sku?: string;
  lastQuoteDate?: unknown;
  notes?: string;
  priceHistory?: HardwarePricePointLike[];
}

const PRICE_EPS = 0.005;

export function unitPricesDiffer(a: number, b: number): boolean {
  return Math.abs((a || 0) - (b || 0)) > PRICE_EPS;
}

export function toHardwareHistoryDate(raw: unknown, fallback: Date = new Date()): Date {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return new Date(raw);
  if (typeof raw === 'string') {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as { toDate?: () => Date; seconds?: number };
    if (typeof o.toDate === 'function') {
      try {
        const d = o.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
      } catch {
        /* ignore */
      }
    }
    if (typeof o.seconds === 'number') return new Date(o.seconds * 1000);
  }
  return fallback;
}

export function normalizeHardwarePriceHistory(
  raw: unknown,
): Array<{ unitPrice: number; recordedAt: Date; source?: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ unitPrice: number; recordedAt: Date; source?: string }> = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const row = p as Record<string, unknown>;
    const unitPrice = Number(row.unitPrice);
    if (!Number.isFinite(unitPrice)) continue;
    const point: { unitPrice: number; recordedAt: Date; source?: string } = {
      unitPrice,
      recordedAt: toHardwareHistoryDate(row.recordedAt),
    };
    if (typeof row.source === 'string' && row.source.trim()) point.source = row.source.trim();
    out.push(point);
  }
  return out;
}

function makePoint(
  unitPrice: number,
  recordedAt: Date,
  source?: HardwarePriceHistorySource,
): { unitPrice: number; recordedAt: Date; source?: HardwarePriceHistorySource } {
  const p: {
    unitPrice: number;
    recordedAt: Date;
    source?: HardwarePriceHistorySource;
  } = { unitPrice, recordedAt };
  if (source) p.source = source;
  return p;
}

/**
 * Apply a new current unitPrice onto a supplierPricing entry, appending history
 * when the price changed. New suppliers get a single initial history point.
 */
export function applyHardwareSupplierPriceWithHistory(
  existing: HardwareSupplierPriceWithHistoryLike | null | undefined,
  next: {
    supplier: string;
    unitPrice: number;
    sku?: string;
    notes?: string;
  },
  opts?: {
    source?: HardwarePriceHistorySource;
    recordedAt?: Date;
  },
): HardwareSupplierPriceWithHistoryLike {
  const recordedAt = opts?.recordedAt || new Date();
  const source = opts?.source || 'manual';
  const supplier = (next.supplier || existing?.supplier || '').trim();
  const unitPrice = Number.isFinite(next.unitPrice) ? next.unitPrice : 0;
  const sku =
    typeof next.sku === 'string' && next.sku.trim()
      ? next.sku.trim()
      : typeof existing?.sku === 'string' && existing.sku.trim()
        ? existing.sku.trim()
        : undefined;

  if (!existing) {
    return {
      supplier,
      unitPrice,
      ...(sku ? { sku } : {}),
      lastQuoteDate: recordedAt,
      ...(typeof next.notes === 'string' && next.notes.trim()
        ? { notes: next.notes.trim() }
        : {}),
      priceHistory: [makePoint(unitPrice, recordedAt, source)],
    };
  }

  const priorHistory = normalizeHardwarePriceHistory(existing.priceHistory);
  const oldPrice = Number.isFinite(existing.unitPrice) ? existing.unitPrice : 0;

  let priceHistory = [...priorHistory];

  if (unitPricesDiffer(oldPrice, unitPrice)) {
    if (priceHistory.length === 0) {
      const seedAt =
        existing.lastQuoteDate != null
          ? toHardwareHistoryDate(existing.lastQuoteDate, recordedAt)
          : recordedAt;
      priceHistory.push(makePoint(oldPrice, seedAt, 'seed'));
    }
    priceHistory.push(makePoint(unitPrice, recordedAt, source));
  } else if (priceHistory.length === 0) {
    priceHistory = [makePoint(unitPrice, recordedAt, source)];
  }

  if (priceHistory.length > HARDWARE_PRICE_HISTORY_CAP) {
    priceHistory = priceHistory.slice(priceHistory.length - HARDWARE_PRICE_HISTORY_CAP);
  }

  const out: HardwareSupplierPriceWithHistoryLike = {
    supplier: supplier || existing.supplier || '',
    unitPrice,
    lastQuoteDate: unitPricesDiffer(oldPrice, unitPrice)
      ? recordedAt
      : existing.lastQuoteDate != null
        ? existing.lastQuoteDate
        : recordedAt,
    priceHistory,
  };
  if (sku) out.sku = sku;
  const notes =
    typeof next.notes === 'string'
      ? next.notes.trim()
      : typeof existing.notes === 'string'
        ? existing.notes.trim()
        : '';
  if (notes) out.notes = notes;
  return out;
}

export function hardwarePercentChange(oldValue: number, newValue: number): number | null {
  if (!Number.isFinite(oldValue) || !Number.isFinite(newValue)) return null;
  if (Math.abs(oldValue) < PRICE_EPS) return null;
  return ((newValue - oldValue) / oldValue) * 100;
}

export function hardwarePriceHistoryPercentChanges(
  history: Array<{ unitPrice: number; recordedAt: Date; source?: string }>,
): Array<{
  fromIndex: number;
  toIndex: number;
  recordedAt: Date;
  unitPrice: number;
  unitPricePct: number | null;
}> {
  const points = normalizeHardwarePriceHistory(history);
  const out: Array<{
    fromIndex: number;
    toIndex: number;
    recordedAt: Date;
    unitPrice: number;
    unitPricePct: number | null;
  }> = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    out.push({
      fromIndex: i - 1,
      toIndex: i,
      recordedAt: cur.recordedAt,
      unitPrice: cur.unitPrice,
      unitPricePct: hardwarePercentChange(prev.unitPrice, cur.unitPrice),
    });
  }
  return out;
}

/** Last N change segments → up to N+1 points for a sparkline. */
export function lastHardwarePriceChangePoints<T extends { unitPrice: number }>(
  history: T[],
  maxChanges = 3,
): T[] {
  if (!history.length) return [];
  const maxPoints = maxChanges + 1;
  if (history.length <= maxPoints) return history;
  return history.slice(history.length - maxPoints);
}

export function hardwarePriceHistorySummary(history: unknown): {
  pointCount: number;
  lastChangePct: number | null;
  lastRecordedAt: string | null;
} {
  const points = normalizeHardwarePriceHistory(history);
  if (points.length === 0) {
    return { pointCount: 0, lastChangePct: null, lastRecordedAt: null };
  }
  const last = points[points.length - 1]!;
  let lastChangePct: number | null = null;
  if (points.length >= 2) {
    lastChangePct = hardwarePercentChange(points[points.length - 2]!.unitPrice, last.unitPrice);
  }
  return {
    pointCount: points.length,
    lastChangePct,
    lastRecordedAt: last.recordedAt.toISOString(),
  };
}
